/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ComponentType } from '@angular/cdk/portal';
import { ComponentRef, Renderer2, TemplateRef, ViewContainerRef } from '@angular/core';

import { GridApi, GridOptions } from 'ag-grid-community';
import type { ProcessRowParams } from 'ag-grid-community';

import { assign } from 'lodash';

import { GRID_EVENT } from '../../grid-public-events';
import { GridFeature, GridFeatureInitParams, GridFeatureReadyParams } from '../feature.interfaces';
import {
  GridRowSelectioniInitParams,
  GridRowSelectionsComponent,
} from './grid-row-selections-component';
import { GridTemplateBasedSelectBarComponent } from './grid-template-based-select-bar.component';

export abstract class AbstractGridSelectBarFeature implements GridFeature {
  order = 25;

  protected renderer: Renderer2;

  protected viewContainerRef: ViewContainerRef;
  protected nativeElement: HTMLElement;
  protected api: GridApi;

  protected suppressedEvents: string[] = ['click'];

  private templateRef: TemplateRef<any>;
  private componentClass: ComponentType<any>;

  initGrid(params: GridFeatureInitParams): void {
    const { viewContainerRef, nativeElement } = params;
    assign(this, { viewContainerRef, nativeElement });
  }

  enhanceGridOptions(options: GridOptions): void {
    options.rowSelection = {
      mode: 'multiRow',
      checkboxes: false,
      headerCheckbox: false,
      enableClickSelection: false,
    };
  }

  onGridReady(params: GridFeatureReadyParams): void {
    this.api = params.api;
  }

  onDestroy(): void {
    // nothing to do
  }

  setSuppressedEvents(suppressedEvents: string[]): void {
    this.suppressedEvents = suppressedEvents;
  }

  setTemplateRef(templateRef: TemplateRef<any>): void {
    this.templateRef = templateRef;
  }

  abstract process(params: ProcessRowParams): void;

  protected addDataChangeListener<C extends GridRowSelectionsComponent>(componentRef: ComponentRef<C>,
                                                                        params: GridRowSelectioniInitParams): void {
    const dataChangedListener = () => {
      const { node, context, api } = params;
      componentRef.instance.refresh({ node, data: node.data, context, api, columnApi: api });
    };

    params.node.addEventListener('dataChanged', dataChangedListener);
    params.addRenderedRowListener(GRID_EVENT.virtualRowRemoved, () => {
      params.node.removeEventListener('dataChanged', dataChangedListener);
    });
  }

  protected getBestLeftPosition(row: GridRowSelectioniInitParams): number {
    return 15;
  }

  protected attachComponent<C extends GridRowSelectionsComponent>(row: GridRowSelectioniInitParams,
                                                                  componentRef: ComponentRef<C>): void {
    this.renderer.addClass(componentRef.location.nativeElement, 'grid-row-options');
    this.renderer.setStyle(componentRef.location.nativeElement, 'left', `${this.getBestLeftPosition(row)}px`);

    this.suppressedEvents.map((eventType) => ({
      type: eventType,
      listener: (event: Event) => event.stopPropagation(),
    })).forEach((event) => {
      componentRef.location.nativeElement.addEventListener(event.type, event.listener);
    });

    componentRef.instance.initRowOptions(row);
    row.eRow.appendChild(componentRef.location.nativeElement);
    this.addDataChangeListener(componentRef, row);
  }

  protected detachComponent(row: GridRowSelectioniInitParams): void {
    // nothing to do
  }

  protected createComponent<C extends GridRowSelectionsComponent>(): ComponentRef<C> {
    const { componentClass, templateRef } = this;
    if (componentClass) {
      return this.viewContainerRef.createComponent(componentClass);
    } else if (templateRef) {
      const selectBar = this.viewContainerRef.createComponent(GridTemplateBasedSelectBarComponent);
      selectBar.instance.setTemplateRef(templateRef);
      return selectBar as any;
    }

    throw new Error('Neither component type nor template ref was defined');
  }
}
